import React, { createContext, ReactNode, useContext } from 'react';
import {
  MediaGalleryEntry,
  NewCartItem,
  SearchResponse
} from '../services/MVP/models';

interface ProductsInformationProps {
  sku: number;
  media_gallery_entries?: MediaGalleryEntry[];
  sap_descricao_gpm?: string;
  descricao_gpm?: string;
}

interface ShoppingCartProps {
  productsInformation: ProductsInformationProps[];
  updateProductsInformation: (
    items: SearchResponse[]
  ) => ProductsInformationProps[];
  cart: NewCartItem[];
  dirtyCart: NewCartItem[];
  updateCartState: (items: NewCartItem[]) => void;
  updateDirtyCartState: (items: NewCartItem[]) => void;
  hasDirtyOnCartState: () => boolean;
}

const defaultCart: ShoppingCartProps = {
  productsInformation: [],
  updateProductsInformation: () => [],
  cart: [],
  dirtyCart: [],
  updateCartState: () => null,
  updateDirtyCartState: () => null,
  hasDirtyOnCartState: () => false
};

const CartContext = createContext<ShoppingCartProps>(defaultCart);

export const ShoppingCartProvider = ({ children }: { children: ReactNode }) => {
  const [productsInformation, setProductsInformation] = React.useState<
    ProductsInformationProps[]
  >([]);
  const [cart, setCart] = React.useState<NewCartItem[]>([]);
  const [dirtyCart, setDirtyCart] = React.useState<NewCartItem[]>([]);

  const updateProductsInformation = (products: SearchResponse[]) => {
    const newProducts = products
      .filter(
        (newItem) =>
          !productsInformation.some(
            (item) => Number(item.sku) === Number(newItem.sku)
          )
      )
      .map((el) => {
        const productImage = [
          {
            file:
              el.media_gallery_entries.length === 0 &&
              el?.extension_attributes?.category_links
                ? el?.extension_attributes?.category_links[0]?.category_image ??
                  ''
                : `/media/catalog/product/${el?.media_gallery_entries[0]?.file}`
          }
        ];

        return {
          sku: Number(el.sku),
          media_gallery_entries: productImage,
          sap_descricao_gpm:
            el?.custom_attributes.find(
              (att) => att.attribute_code === 'sap_descricao_gpm'
            )?.value ?? '',
          descricao_gpm:
            el.custom_attributes.find(
              (att) => att.attribute_code === 'descricao_gpm'
            )?.value ?? ''
        };
      })
      .filter(
        (item) =>
          !!item.sap_descricao_gpm &&
          item.sap_descricao_gpm !== '' &&
          !!item.descricao_gpm &&
          item.descricao_gpm !== ''
      );

    const newProductsInformation = [...productsInformation, ...newProducts].map(
      (el) => ({
        sku: Number(el.sku),
        media_gallery_entries: el.media_gallery_entries,
        sap_descricao_gpm: el.sap_descricao_gpm,
        descricao_gpm: el.descricao_gpm
      })
    );

    setProductsInformation(newProductsInformation);

    return newProductsInformation;
  };

  const updateCartState = (items: NewCartItem[]) => {
    if (items) {
      const adjustedItems = items.map((item) => {
        const cartFound = cart.find((cartItem) => cartItem.sku === item.sku);
        if (
          cartFound?.extension_attributes.tax_benefit_gerdau &&
          !item.extension_attributes.tax_benefit_gerdau
        ) {
          item.extension_attributes.tax_benefit_gerdau =
            cartFound?.extension_attributes.tax_benefit_gerdau;
        }
        return item;
      });
      setCart(adjustedItems);
      setDirtyCart(adjustedItems);
    }
  };

  const updateDirtyCartState = (items: NewCartItem[]) => {
    if (items) {
      setDirtyCart(items);
    }
  };

  const hasDirtyOnCartState = () => {
    if (cart.length !== dirtyCart.length) {
      return true;
    }

    const sortedCart = cart.map((obj) => JSON.stringify(obj)).sort();
    const sortedDirtyCart = dirtyCart.map((obj) => JSON.stringify(obj)).sort();

    return JSON.stringify(sortedCart) !== JSON.stringify(sortedDirtyCart);
  };

  return (
    <CartContext.Provider
      value={{
        productsInformation,
        updateProductsInformation,
        cart,
        dirtyCart,
        updateCartState,
        updateDirtyCartState,
        hasDirtyOnCartState
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useShoppingCart = () => useContext(CartContext);
